/**
 * Redige valores sensíveis antes de entrarem no window.dataLayer (GA4/GTM form tracking).
 *
 * Instalação única em index.tsx; após o gtag.js carregar, chame
 * ensureDataLayerSanitizer() — reenvólve só se o script tiver substituído push.
 *
 * Importante: NÃO percorrer host objects (DOM, Window). O GA enhanced measurement
 * empurra forms/elementos com `.length`; tratá-los como Arguments causa
 * Maximum call stack size exceeded dentro do gtag.js.
 */

const SENSITIVE_FIELD_RE =
  /password|senha|confirm-password|email|e-mail|phone|telefone|tel|cpf|ssn|token|secret/i;
const FORM_GTM_EVENT_RE = /form|interact|field|element/i;

/** Marca no push para não empilhar wrappers. */
const SANITIZED_PUSH = Symbol('adliticsDataLayerSanitized');

type DataLayer = unknown[] & {
  push: ((...args: unknown[]) => number) & { [SANITIZED_PUSH]?: true };
};

function isSensitiveFieldId(fieldId: unknown): boolean {
  return typeof fieldId === 'string' && SENSITIVE_FIELD_RE.test(fieldId);
}

function shouldRedactValue(obj: Record<string, unknown>): boolean {
  if (!('value' in obj) || obj.value == null || obj.value === '') return false;

  const fieldId =
    obj['gtm.interactedFormFieldId'] ?? obj.form_field_id ?? obj.field_id ?? obj.fieldId;
  if (isSensitiveFieldId(fieldId)) return true;

  const eventName = obj.event;
  if (typeof eventName === 'string' && FORM_GTM_EVENT_RE.test(eventName)) {
    if (isSensitiveFieldId(fieldId)) return true;
    if (fieldId == null) return true;
  }

  return false;
}

/** Evita stack overflow em grafos profundos (além de ciclos). */
const MAX_SANITIZE_DEPTH = 20;

function isArgumentsObject(value: unknown): boolean {
  return Object.prototype.toString.call(value) === '[object Arguments]';
}

/** Só objetos literais — não DOM/Window/Date/etc. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function isHostObject(value: object): boolean {
  if (typeof Node !== 'undefined' && value instanceof Node) return true;
  if (typeof Window !== 'undefined' && value instanceof Window) return true;
  if (typeof Element !== 'undefined' && value instanceof Element) return true;
  return false;
}

/** Redige entradas do dataLayer (objeto plano, array ou Arguments do gtag). */
export function sanitizeDataLayerEntry(
  entry: unknown,
  seen = new WeakSet<object>(),
  depth = 0,
): unknown {
  if (entry == null || depth > MAX_SANITIZE_DEPTH) return entry;
  if (typeof entry !== 'object') return entry;

  // Host objects (form, input, window): não clonar / não recursar.
  if (isHostObject(entry)) return entry;

  if (seen.has(entry)) return '[Circular]';
  seen.add(entry);

  if (Array.isArray(entry) || isArgumentsObject(entry)) {
    return Array.from(entry as ArrayLike<unknown>).map((item) =>
      sanitizeDataLayerEntry(item, seen, depth + 1),
    );
  }

  // Objetos com `.length` que NÃO são Arguments (ex.: HTMLFormElement) — deixar intactos.
  if (!isPlainObject(entry)) return entry;

  const next: Record<string, unknown> = { ...entry };

  if (shouldRedactValue(next)) {
    next.value = '[REDACTED]';
  }

  for (const key of Object.keys(next)) {
    if (key === 'value') continue;
    const val = next[key];
    if (val && typeof val === 'object') {
      next[key] = sanitizeDataLayerEntry(val, seen, depth + 1);
    }
  }

  return next;
}

function wrapDataLayerPush(dataLayer: DataLayer): void {
  if (dataLayer.push[SANITIZED_PUSH]) return;

  const original = dataLayer.push.bind(dataLayer);
  const wrapped = ((...args: unknown[]) =>
    original(...args.map((arg) => sanitizeDataLayerEntry(arg)))) as DataLayer['push'];
  wrapped[SANITIZED_PUSH] = true;
  dataLayer.push = wrapped;
}

/**
 * Garante window.dataLayer com push sanitizado.
 * Idempotente — não empilha wrappers se o push já for o nosso.
 */
export function installDataLayerSanitizer(): void {
  if (typeof window === 'undefined') return;

  const dl = (window.dataLayer ?? []) as DataLayer;
  window.dataLayer = dl;
  wrapDataLayerPush(dl);
}

/**
 * Reaplica sanitização se o gtag.js substituiu dataLayer.push.
 * Seguro chamar várias vezes (idempotente).
 */
export function ensureDataLayerSanitizer(): void {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  wrapDataLayerPush(window.dataLayer as DataLayer);
}
